import type { FC, ReactNode } from "react";

import type { RealtimeTaskUsage } from "../../hooks/useRealtimeTaskUsage";

import { formatTokenCount, formatUsd } from "../../utils/currency";

interface LiveUsageMetricsProps {
  usage: RealtimeTaskUsage;
  isTaskRunning: boolean;
}

interface PrimaryMetricProps {
  icon: ReactNode;
  label: string;
  value: string;
}

const PrimaryMetric: FC<PrimaryMetricProps> = ({ icon, label, value }) => (
  <div className="flex min-w-0 items-start gap-2.5 px-3 py-2.5 sm:px-4">
    <div className="mt-0.5 shrink-0 text-slate-400 dark:text-slate-500">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-0.5 font-mono text-base font-bold text-slate-900 dark:text-slate-100 sm:text-lg">
        {value}
      </div>
    </div>
  </div>
);

interface BreakdownMetricProps {
  label: string;
  value: string;
  accentClassName?: string;
}

const BreakdownMetric: FC<BreakdownMetricProps> = ({
  label,
  value,
  accentClassName,
}) => (
  <div className="min-w-0 border-l border-slate-300 px-3 py-2 first:border-l-0 dark:border-slate-700">
    <div className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-500">
      {label}
    </div>
    <div
      className={`mt-1 font-mono text-sm font-bold sm:text-[15px] ${accentClassName ?? "text-slate-700 dark:text-slate-200"}`}
    >
      {value}
    </div>
  </div>
);

export const LiveUsageMetrics: FC<LiveUsageMetricsProps> = ({
  usage,
  isTaskRunning,
}) => {
  if (!usage.hasData) {
    return null;
  }

  const showLiveIndicator = usage.isRealtime && isTaskRunning;
  const totalTokens = formatTokenCount(usage.totalTokens);
  const inputTokens = formatTokenCount(usage.inputTokens);
  const outputTokens = formatTokenCount(usage.outputTokens);
  const cachedTokens =
    usage.cachedTokens > 0 ? formatTokenCount(usage.cachedTokens) : null;

  return (
    <section className="border-2 border-slate-300 bg-slate-100 dark:border-slate-700 dark:bg-arti-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-300 px-3 py-2.5 dark:border-slate-700 sm:px-4">
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
          Usage Snapshot
        </div>
        <div
          className={`inline-flex items-center gap-2 border px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] ${
            showLiveIndicator
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : "border-slate-300 bg-white text-slate-500 dark:border-slate-700 dark:bg-arti-background dark:text-slate-400"
          }`}
        >
          <span className="relative flex h-2.5 w-2.5">
            {showLiveIndicator ? (
              <>
                <span className="absolute inline-flex h-full w-full animate-ping bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 bg-emerald-500" />
              </>
            ) : (
              <span className="relative inline-flex h-2.5 w-2.5 bg-slate-400 dark:bg-slate-500" />
            )}
          </span>
          {showLiveIndicator ? "Live" : "Final"}
        </div>
      </div>

      <div className="border-b border-slate-300 bg-white dark:border-slate-700 dark:bg-arti-background">
        <div className="grid divide-y divide-slate-300 dark:divide-slate-700 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
          <PrimaryMetric
            label="Total Tokens"
            value={`${totalTokens} tokens`}
            icon={
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                  strokeWidth={2}
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                />
              </svg>
            }
          />
          {usage.estimatedCostUsd !== null ? (
            <PrimaryMetric
              label="Estimated Cost"
              value={formatUsd(usage.estimatedCostUsd)}
              icon={
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
            />
          ) : (
            <PrimaryMetric
              label="Estimated Cost"
              value="Pending"
              icon={
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                    strokeWidth={2}
                    d="M12 6v6l4 2m5-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
            />
          )}
        </div>
      </div>

      <div
        className={`grid bg-white dark:bg-arti-background ${
          cachedTokens ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2"
        }`}
      >
        <BreakdownMetric label="Input" value={`${inputTokens} in`} />
        <BreakdownMetric label="Output" value={`${outputTokens} out`} />
        {cachedTokens ? (
          <BreakdownMetric
            label="Cached"
            value={`${cachedTokens} cached`}
            accentClassName="text-emerald-600 dark:text-emerald-400"
          />
        ) : null}
      </div>
    </section>
  );
};
